import React from "react";
import { ChevronRight } from "lucide-react";
import { LinkAnchor } from "../../components/ui/link";
import { ContractorSeoLayout } from "./ContractorSeoLayout";
import { ContractorSeoCta } from "./ContractorSeoCta";
import type { ContractorSeoProjectPageProps } from "./types";

const Breadcrumbs: React.FC<{
  items: ContractorSeoProjectPageProps["breadcrumbs"];
}> = ({ items }) => (
  <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
    <ol className="flex flex-wrap items-center gap-1">
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1;
        return (
          <li key={`${item.url}-${idx}`} className="flex items-center gap-1">
            {isLast ? (
              <span aria-current="page" className="text-foreground">
                {item.name}
              </span>
            ) : (
              <LinkAnchor
                href={item.url}
                className="hover:text-accent hover:no-underline"
              >
                {item.name}
              </LinkAnchor>
            )}
            {!isLast ? (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            ) : null}
          </li>
        );
      })}
    </ol>
  </nav>
);

export const ContractorSeoProjectPage: React.FC<
  ContractorSeoProjectPageProps
> = (props) => {
  const {
    h1,
    bodyHtml,
    imageUrl,
    imageAlt,
    category,
    relatedServiceHref,
    breadcrumbs,
    business,
  } = props;

  return (
    <ContractorSeoLayout {...props}>
      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          {h1}
        </h1>
        {category ? (
          <p className="mt-2 text-sm uppercase tracking-wide text-muted-foreground">
            {category}
          </p>
        ) : null}

        {imageUrl ? (
          <figure className="mt-8 overflow-hidden rounded-lg border border-border bg-muted">
            <img
              src={imageUrl}
              alt={imageAlt}
              className="h-auto w-full object-cover"
              loading="eager"
              decoding="async"
            />
          </figure>
        ) : null}

        <div
          className="mt-8 space-y-4 text-base leading-relaxed text-foreground prose-seo"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />

        {relatedServiceHref ? (
          <p className="mt-8 text-sm text-muted-foreground">
            See more{" "}
            <LinkAnchor
              href={relatedServiceHref}
              className="font-semibold text-primary hover:text-accent"
            >
              {category ?? "related"} work
            </LinkAnchor>{" "}
            from {business.businessName}.
          </p>
        ) : null}

        <ContractorSeoCta
          business={business}
          heading="Have a similar project?"
        />
      </article>
    </ContractorSeoLayout>
  );
};
